import React, { useState, useRef, useEffect, forwardRef } from 'react';
import './DatePicker.css';

const DatePicker = forwardRef(({ selectedDate, onSelect, onClose, min, max, positionAbove = false, position }, forwardedRef) => {
  const [viewMode, setViewMode] = useState('day'); // 'day', 'month', or 'year'
  const [displayMonth, setDisplayMonth] = useState(() => {
    if (selectedDate) {
      const date = new Date(selectedDate + 'T00:00:00');
      return date.getMonth() + 1;
    }
    return new Date().getMonth() + 1;
  });
  const [displayYear, setDisplayYear] = useState(() => {
    if (selectedDate) {
      const date = new Date(selectedDate + 'T00:00:00');
      return date.getFullYear();
    }
    return new Date().getFullYear();
  });
  const pickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const currentDay = currentDate.getDate();

  // Get days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month, 0).getDate();
  };

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month - 1, 1).getDay();
  };

  // Check if date is disabled
  const isDateDisabled = (day, month, year) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    if (min) {
      if (dateStr < min) return true;
    }
    if (max) {
      if (dateStr > max) return true;
    }
    return false;
  };

  // Check if month is disabled
  const isMonthDisabled = (month, year) => {
    if (min) {
      const minDate = new Date(min + 'T00:00:00');
      const minYear = minDate.getFullYear();
      const minMonth = minDate.getMonth() + 1;
      if (year < minYear) return true;
      if (year === minYear && month < minMonth) return true;
    }
    if (max) {
      const maxDate = new Date(max + 'T00:00:00');
      const maxYear = maxDate.getFullYear();
      const maxMonth = maxDate.getMonth() + 1;
      if (year > maxYear) return true;
      if (year === maxYear && month > maxMonth) return true;
    }
    return false;
  };

  // Check if year is disabled
  const isYearDisabled = (year) => {
    if (min) {
      const minDate = new Date(min + 'T00:00:00');
      if (year < minDate.getFullYear()) return true;
    }
    if (max) {
      const maxDate = new Date(max + 'T00:00:00');
      if (year > maxDate.getFullYear()) return true;
    }
    return false;
  };

  // Navigate month
  const navigateMonth = (direction) => {
    if (direction === 'prev') {
      if (displayMonth === 1) {
        setDisplayMonth(12);
        setDisplayYear(displayYear - 1);
      } else {
        setDisplayMonth(displayMonth - 1);
      }
    } else {
      if (displayMonth === 12) {
        setDisplayMonth(1);
        setDisplayYear(displayYear + 1);
      } else {
        setDisplayMonth(displayMonth + 1);
      }
    }
  };

  // Check if can navigate
  const canNavigatePrev = () => {
    if (displayMonth === 1 && displayYear === currentYear - 10) {
      if (min) {
        const minDate = new Date(min + 'T00:00:00');
        return displayYear > minDate.getFullYear() || 
               (displayYear === minDate.getFullYear() && 12 > minDate.getMonth());
      }
      return true;
    }
    if (min) {
      const minDate = new Date(min + 'T00:00:00');
      const minYear = minDate.getFullYear();
      const minMonth = minDate.getMonth() + 1;
      if (displayYear < minYear) return false;
      if (displayYear === minYear && displayMonth <= minMonth) return false;
    }
    return true;
  };

  const canNavigateNext = () => {
    if (max) {
      const maxDate = new Date(max + 'T00:00:00');
      const maxYear = maxDate.getFullYear();
      const maxMonth = maxDate.getMonth() + 1;
      if (displayYear > maxYear) return false;
      if (displayYear === maxYear && displayMonth >= maxMonth) return false;
    }
    return true;
  };

  // Handle day select
  const handleDaySelect = (day) => {
    const dateStr = `${displayYear}-${String(displayMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (!isDateDisabled(day, displayMonth, displayYear)) {
      onSelect(dateStr);
      onClose();
    }
  };

  // Handle month select (from month view)
  const handleMonthSelect = (month) => {
    if (!isMonthDisabled(month, displayYear)) {
      setDisplayMonth(month);
      setViewMode('day');
    }
  };

  // Handle year select (from year view)
  const handleYearSelect = (year) => {
    if (!isYearDisabled(year)) {
      setDisplayYear(year);
      setViewMode('month');
    }
  };

  // Render calendar days
  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(displayMonth, displayYear);
    const firstDay = getFirstDayOfMonth(displayMonth, displayYear);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${displayYear}-${String(displayMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isSelected = selectedDate === dateStr;
      const isCurrentDay = day === currentDay && displayMonth === currentMonth && displayYear === currentYear;
      const isDisabled = isDateDisabled(day, displayMonth, displayYear);

      days.push(
        <button
          key={day}
          type="button"
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isCurrentDay ? 'current' : ''} ${isDisabled ? 'disabled' : ''}`}
          onClick={() => handleDaySelect(day)}
          disabled={isDisabled}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  // Generate years for year view
  const getYearRange = () => {
    const startYear = Math.floor(displayYear / 10) * 10;
    return Array.from({ length: 10 }, (_, i) => startYear + i);
  };

  // Apply position styles if provided (for fixed positioning)
  const positionStyle = position ? {
    top: position.top !== undefined ? `${position.top}px` : undefined,
    left: position.left !== undefined ? `${position.left}px` : undefined,
    right: position.right !== undefined && position.right !== 'auto' ? `${position.right}px` : undefined
  } : {};

  if (viewMode === 'day') {
    return (
      <div className={`date-picker ${positionAbove ? 'date-picker-above' : ''}`} ref={pickerRef} style={positionStyle}>
        <div className="picker-header">
          <button
            type="button"
            className={`picker-nav-btn ${!canNavigatePrev() ? 'disabled' : ''}`}
            onClick={() => canNavigatePrev() && navigateMonth('prev')}
            disabled={!canNavigatePrev()}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="picker-date-header">
            <button
              type="button"
              className="picker-month-btn"
              onClick={() => setViewMode('month')}
            >
              {months[displayMonth - 1]}
            </button>
            <button
              type="button"
              className="picker-year-btn"
              onClick={() => setViewMode('year')}
            >
              {displayYear}
            </button>
          </div>
          <button
            type="button"
            className={`picker-nav-btn ${!canNavigateNext() ? 'disabled' : ''}`}
            onClick={() => canNavigateNext() && navigateMonth('next')}
            disabled={!canNavigateNext()}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="calendar-weekdays">
          {weekDays.map(day => (
            <div key={day} className="calendar-weekday">{day}</div>
          ))}
        </div>
        <div className="calendar-days">
          {renderCalendarDays()}
        </div>
      </div>
    );
  } else if (viewMode === 'month') {
    const selectedMonth = selectedDate ? new Date(selectedDate + 'T00:00:00').getMonth() + 1 : null;
    
    return (
      <div className={`date-picker ${positionAbove ? 'date-picker-above' : ''}`} ref={pickerRef} style={positionStyle}>
        <div className="picker-header">
          <button
            type="button"
            className="picker-nav-btn"
            onClick={() => setDisplayYear(displayYear - 1)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            type="button"
            className="picker-year-btn"
            onClick={() => setViewMode('year')}
          >
            {displayYear}
          </button>
          <button
            type="button"
            className="picker-nav-btn"
            onClick={() => setDisplayYear(displayYear + 1)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="picker-months">
          {months.map((month, index) => {
            const monthNum = index + 1;
            const isSelected = monthNum === selectedMonth && displayYear === (selectedDate ? new Date(selectedDate + 'T00:00:00').getFullYear() : null);
            const isCurrentMonth = monthNum === currentMonth && displayYear === currentYear;
            const isDisabled = isMonthDisabled(monthNum, displayYear);

            return (
              <button
                key={monthNum}
                type="button"
                className={`picker-month-item ${isSelected ? 'selected' : ''} ${isCurrentMonth ? 'current' : ''} ${isDisabled ? 'disabled' : ''}`}
                onClick={() => handleMonthSelect(monthNum)}
                disabled={isDisabled}
              >
                {month.substring(0, 3)}
              </button>
            );
          })}
        </div>
      </div>
    );
  } else {
    // Year view
    const yearRange = getYearRange();
    const selectedYear = selectedDate ? new Date(selectedDate + 'T00:00:00').getFullYear() : null;

    return (
      <div className={`date-picker ${positionAbove ? 'date-picker-above' : ''}`} ref={pickerRef} style={positionStyle}>
        <div className="picker-header">
          <button
            type="button"
            className="picker-nav-btn"
            onClick={() => setDisplayYear(displayYear - 10)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <span className="picker-year-range">
            {yearRange[0]} - {yearRange[9]}
          </span>
          <button
            type="button"
            className="picker-nav-btn"
            onClick={() => setDisplayYear(displayYear + 10)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="picker-years">
          {yearRange.map((year) => {
            const isSelected = year === selectedYear;
            const isCurrentYear = year === currentYear;
            const isDisabled = isYearDisabled(year);

            return (
              <button
                key={year}
                type="button"
                className={`picker-year-item ${isSelected ? 'selected' : ''} ${isCurrentYear ? 'current' : ''} ${isDisabled ? 'disabled' : ''}`}
                onClick={() => handleYearSelect(year)}
                disabled={isDisabled}
              >
                {year}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
});

DatePicker.displayName = 'DatePicker';

export default DatePicker;

